<template>
  <div
    v-if="showActions"
    data-component="app-launcher-warning-actions"
  >
    <or-code
      v-if="showError"
      :model-value="code"
      :readonly="true"
      :enable-fullscreen="false"
      language="json"
    />

    <or-button
      v-if="appState === 'warning-no-app-available'"
      data-button="install"
      :loading="isUpdating"
      @click="$emit('app-install')"
    >
      {{ $t('HITL.common.actions.install') }}
    </or-button>

    <div
      v-if="appState === 'warning-no-app-version'"
      class="actions"
    >
      <or-button
        data-button="go-to-launcher"
        variant="outlined"
        @click="$emit('go-to-launcher')"
      >
        {{ $t('HITL.modules.app-header.back-to-launcher') }}
      </or-button>
      <or-button
        data-button="accept"
        :loading="isLoading"
        @click="$emit('accept-version')"
      >
        {{ useVersionLabel }}
      </or-button>
    </div>

    <div
      v-if="appState === 'warning-no-app-version-support'"
      class="actions"
    >
      <or-button
        data-button="update"
        :loading="isUpdating"
        @click="$emit('app-update')"
      >
        {{ useVersionLabel }}
      </or-button>
    </div>

    <div
      v-if="appState === 'warning-data-update-error-admin'"
      class="actions"
    >

      <or-button
        data-button="go-to-launcher"
        variant="outlined"
        @click="$emit('go-to-launcher')"
      >
        {{ $t('HITL.modules.app-header.back-to-launcher') }}
      </or-button>
      <or-button
        v-if="vSuggest"
        data-button="accept"
        variant="outlined"
        :loading="isLoading"
        :disabled="isUpdating"
        @click="$emit('accept-version')"
      >
        {{ useVersionLabel }}
      </or-button>
      <or-button
        data-button="retry"
        :loading="isUpdating"
        :disabled="isLoading"
        @click="$emit('retry-update')"
      >
        {{ $t('HITL.common.actions.retry') }}
      </or-button>
    </div>

    <div
      v-if="appState === 'warning-needs-data-update'"
      class="actions"
    >
      <or-button
        data-button="cancel"
        variant="outlined"
        :disabled="isUpdating"
        @click="$emit('cancel')"
      >
        {{ $t('HITL.common.actions.cancel') }}
      </or-button>
      <or-button
        data-button="update"
        :loading="isUpdating"
        @click="$emit('update')"
      >
        {{ $t('HITL.common.actions.update') }}
      </or-button>
    </div>

    <or-button
      v-if="showBackToLauncher"
      data-button="go-to-launcher"
      class="go-to-launcher-button"
      @click="$emit('go-to-launcher')"
    >
      {{ $t('HITL.modules.app-header.back-to-launcher') }}
    </or-button>
  </div>
</template>

<script lang="ts">
import _ from 'lodash';
import { defineComponent } from 'vue-demi';
import {
  OrCodeV3 as OrCode,
  OrButtonV3 as OrButton,
} from '@onereach/ui-components-25';

import { SDKError } from '../types';
import { APP_STATES, APP_STATE_PROP, APP_ERROR_PROP } from '../constants';

export default defineComponent({
  name: 'AppLauncherWarningActions',
  components: { OrCode, OrButton },
  props: {
    vSuggest: {
      type: String,
      default: '',
    },
    appState: APP_STATE_PROP,
    appError: APP_ERROR_PROP,
    isUpdating: Boolean,
    isLoading: Boolean,
    isUserAnAdmin: Boolean,
  },
  emits: ['app-install', 'app-update', 'retry-update', 'accept-version', 'update', 'cancel', 'go-to-launcher'],
  computed: {
    /** When Back to Launcher is alone (no other action in same row), show as standalone button */
    showBackToLauncher() {
      if (!this.isUserAnAdmin) return false;
      return [
        APP_STATES.NO_AGENT_ACCESS,
        APP_STATES.APP_ERROR,
      ].includes(this.appState);
    },

    showActions() {
      return [
        APP_STATES.NO_APP_AVAILABLE,
        APP_STATES.NO_APP_VERSION,
        APP_STATES.NO_APP_VERSION_SUPPORT,
        APP_STATES.NO_AGENT_ACCESS,
        APP_STATES.DATA_UPDATE_ERROR_ADMIN,
        APP_STATES.NEEDS_DATA_UPDATE,
        APP_STATES.APP_ERROR,
      ].includes(this.appState);
    },

    showError() {
      const states = [APP_STATES.DATA_UPDATE_ERROR_ADMIN, APP_STATES.APP_ERROR];
      return states.includes(this.appState);
    },

    useVersionLabel() {
      const LABELS: Record<string, string> = {
        [APP_STATES.NO_APP_VERSION]: 'HITL.app-launcher.actions.use-version',
        [APP_STATES.DATA_UPDATE_ERROR_ADMIN]: 'HITL.app-launcher.actions.use-version',
        [APP_STATES.NO_APP_VERSION_SUPPORT]: 'HITL.app-launcher.actions.update-to-version',
      };

      const content = { version: this.vSuggest };
      return this.$t(LABELS[this.appState], content);
    },

    code() {
      const { message, details, stack } = (this.appError || {}) as SDKError;
      if (!_.isEmpty(details)) {
        const obj: SDKError = { ...this.appError };
        obj.timestamp = new Date(obj.timestamp || Date.now()).toLocaleString();
        return JSON.stringify(obj, null, 2);
      }

      return JSON.stringify({ message, stack }, null, 2);
    },
  },
});
</script>

<style lang="scss">
@use '@/styles' as *;

[data-component='app-launcher-warning-actions'] {
  --app-launcher-warning-buttons-layout: row-reverse;
  --app-launcher-warning-button-width: 12.5rem;

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  width: 100%;

  @include max-md {
    --app-launcher-warning-buttons-layout: column;
    --app-launcher-warning-button-width: 100%;
  }

  .actions {
    display: flex;
    flex-direction: var(--app-launcher-warning-buttons-layout);
    justify-content: center;
    gap: var(--s-4);
    width: 100%;

    [data-button] {
      width: var(--app-launcher-warning-button-width);
    }
  }

  .go-to-launcher-button {
    margin-top: var(--s-4);
  }

  .or-code-v3 {
    width: 100%;
    max-height: 22.5rem;
  }
}
</style>
